// Prise de rendez-vous publique : plages définies par moi, créneaux disponibles, validation d’une demande.
// Disponibilité = mes plages ∩ pas déjà réservé (avec marge) ∩ délai minimum ∩ horizon.
// Phase 4 ajoutera l’agenda Google dans les créneaux occupés.
import { DAY, MIN, nextDate, todayIn } from './time';
import { localInstant, type Interval } from './window';
import { cleanText } from './contact';

export interface BookingWindow {
  /** 0 = dimanche … 6 = samedi, dans mon fuseau. */
  day: number;
  /** Minutes locales depuis minuit. */
  start: number;
  end: number;
}

export interface BookingConfig {
  tz: string;
  duration: number;
  buffer: number;
  noticeHours: number;
  horizonDays: number;
  windows: BookingWindow[];
}

export interface BookingSettings extends Omit<BookingConfig, 'tz'> {
  enabled: boolean;
  title: string;
  introFr: string;
  introEn: string;
  /** Chaque demande attend votre accord avant d’être confirmée, plutôt que de l’être aussitôt. */
  requireApproval: boolean;
}

export interface BookingPublic extends BookingConfig {
  owner: string;
  displayName: string;
  title: string;
  introFr: string;
  introEn: string;
  requireApproval: boolean;
  busy: Interval[];
}

export interface Booking {
  id: string;
  startAt: number;
  endAt: number;
  name: string;
  email: string;
  note: string;
  tz: string;
  lang: 'fr' | 'en';
  /** en attente : demande envoyée, pas encore décidée. refusée : décidée, mais pas acceptée. */
  status: 'confirmed' | 'pending' | 'declined' | 'cancelled';
  createdAt: string;
  meetUrl: string | null;
  /** Événement créé dans Google Agenda (invitation envoyée), sinon null. */
  googleEventId: string | null;
  /** Invités ajoutés par la personne qui réserve : ils reçoivent l’invitation et le rappel, pas le lien d’annulation. */
  guests: string[];
}

export const DURATIONS = [15, 20, 30, 45, 60, 90] as const;

export const DEFAULT_TITLE = 'Visio · Acte 5';
export const DEFAULT_INTRO_FR = 'Un premier échange en visio, pour faire connaissance et parler de votre projet. Choisissez le créneau qui vous arrange, dans votre fuseau : le lien de visioconférence vous parvient avant l’appel. Votre projet reste confidentiel. Venez avec vos idées, vos références et vos questions.';
export const DEFAULT_INTRO_EN = 'A first video call to get acquainted and talk about your project. Pick the slot that suits you, in your own time zone: the video link reaches you before the call. Your project stays confidential. Bring your ideas, references and questions.';

/** Anciennes réservations seulement : la demande de confidentialité a été retirée (trop tôt dans le
    processus), mais son repère peut encore préfixer la note de réservations passées. */
const LEGACY_NDA_MARK = '[NDA] ';

export function cleanLegacyNote(note: string): string {
  return note.startsWith(LEGACY_NDA_MARK) ? note.slice(LEGACY_NDA_MARK.length) : note;
}

export const DEFAULT_BOOKING: BookingSettings = {
  enabled: false, duration: 30, buffer: 15, noticeHours: 24, horizonDays: 30, windows: [],
  title: DEFAULT_TITLE, introFr: DEFAULT_INTRO_FR, introEn: DEFAULT_INTRO_EN, requireApproval: false,
};

/** Périodes ouvertes à la réservation, en instants, couvrant [from, to]. */
export function windowIntervals(cfg: BookingConfig, from: number, to: number): Interval[] {
  const out: Interval[] = [];
  let date = nextDate(todayIn(cfg.tz, from), -1);
  const last = todayIn(cfg.tz, to);
  while (date <= last) {
    const dow = new Date(date + 'T12:00:00Z').getUTCDay();
    for (const w of cfg.windows) {
      if (w.day !== dow || w.end <= w.start) continue;
      const start = localInstant(date, w.start, cfg.tz), end = localInstant(date, w.end, cfg.tz);
      if (end > from && start < to) out.push({ start, end });
    }
    date = nextDate(date);
  }
  return out.sort((a, b) => a.start - b.start);
}

/** Pas entre deux propositions : le quart d’heure ou la demi-heure, jamais plus. */
export function slotStep(duration: number): number {
  return Math.min(duration, 30) * MIN;
}

/** Créneaux réservables à partir de `now`, dans l’ordre. `opts.from`/`opts.days` restreignent la fenêtre affichée. */
export function availableSlots(cfg: BookingConfig, busy: Interval[], now: number, opts: { from?: number; days?: number } = {}): Interval[] {
  const minStart = Math.max(now + cfg.noticeHours * 3_600_000, opts.from ?? now);
  const maxEnd = Math.min(now + cfg.horizonDays * DAY, opts.days !== undefined ? (opts.from ?? now) + opts.days * DAY : Number.POSITIVE_INFINITY);
  if (minStart >= maxEnd) return [];
  const step = slotStep(cfg.duration), len = cfg.duration * MIN;
  const blocked = busy.map((b) => ({ start: b.start - cfg.buffer * MIN, end: b.end + cfg.buffer * MIN }));
  const out: Interval[] = [];
  for (const w of windowIntervals(cfg, minStart, maxEnd)) {
    for (let t = w.start; t + len <= w.end; t += step) {
      const end = t + len;
      if (t < minStart || end > maxEnd) continue;
      if (blocked.some((b) => t < b.end && end > b.start)) continue;
      out.push({ start: t, end });
    }
  }
  return out;
}

/** Une demande est acceptable si son début est exactement un créneau disponible. */
export function bookable(cfg: BookingConfig, busy: Interval[], start: number, now: number): Interval | null {
  return availableSlots(cfg, busy, now).find((s) => s.start === start) ?? null;
}

export function groupByDay(slots: Interval[], tz: string): Array<{ date: string; slots: Interval[] }> {
  const map = new Map<string, Interval[]>();
  for (const s of slots) {
    const d = todayIn(tz, s.start);
    if (!map.has(d)) map.set(d, []);
    map.get(d)!.push(s);
  }
  return [...map.entries()].map(([date, list]) => ({ date, slots: list }));
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function int(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isInteger(n) ? n : fallback;
}

export function validateWindows(input: unknown): BookingWindow[] {
  if (!Array.isArray(input)) return [];
  const out: BookingWindow[] = [];
  for (const w of input) {
    if (!isRecord(w)) continue;
    const day = int(w.day, -1), start = int(w.start, -1), end = int(w.end, -1);
    if (day < 0 || day > 6 || start < 0 || start > 1439 || end < 1 || end > 1440 || end <= start) continue;
    out.push({ day, start, end });
  }
  return out.sort((a, b) => a.day - b.day || a.start - b.start);
}

/** Valide et normalise les réglages (formulaire ou serveur). */
export function validateBookingSettings(input: unknown): BookingSettings {
  const r = isRecord(input) ? input : {};
  const duration = int(r.duration, 30);
  return {
    enabled: r.enabled === true,
    duration: (DURATIONS as readonly number[]).includes(duration) ? duration : 30,
    buffer: Math.min(120, Math.max(0, int(r.buffer, 15))),
    noticeHours: Math.min(720, Math.max(0, int(r.noticeHours, 24))),
    horizonDays: Math.min(90, Math.max(1, int(r.horizonDays, 30))),
    windows: validateWindows(r.windows),
    title: cleanText(r.title, 80) || DEFAULT_TITLE,
    introFr: cleanText(r.introFr, 600),
    introEn: cleanText(r.introEn, 600),
    requireApproval: r.requireApproval === true,
  };
}

/** Lecture de la réponse publique du serveur (booking_public), tolérante. */
export function parseBookingPublic(raw: unknown): BookingPublic | null {
  if (!isRecord(raw) || typeof raw.owner !== 'string' || typeof raw.tz !== 'string') return null;
  const s = validateBookingSettings({ ...raw, enabled: true });
  const busy: Interval[] = Array.isArray(raw.busy)
    ? raw.busy.filter(isRecord).map((b) => ({ start: Number(b.start), end: Number(b.end) })).filter((b) => Number.isFinite(b.start) && Number.isFinite(b.end) && b.end > b.start)
    : [];
  return { owner: raw.owner, tz: raw.tz, displayName: cleanText(raw.displayName, 60), duration: s.duration, buffer: s.buffer, noticeHours: s.noticeHours, horizonDays: s.horizonDays, windows: s.windows, title: s.title, introFr: s.introFr, introEn: s.introEn, requireApproval: raw.requireApproval === true, busy };
}

export const cleanBookingEmail = (v: unknown): string => {
  const s = cleanText(v, 120);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? s.toLowerCase() : '';
};

/** Invités au plus, en plus de la personne qui réserve. Les e-mails partent du compte Google du propriétaire :
    la limite borne ce qu’un formulaire public peut faire envoyer. */
export const MAX_GUESTS = 4;

/**
 * Adresses des invités : nettoyées, en minuscules, sans doublon ni l’adresse de la personne qui réserve, champs
 * vides ignorés. `null` si l’une est invalide ou s’il y en a plus que `MAX_GUESTS` : la demande est alors refusée
 * plutôt que tronquée en silence (quelqu’un croirait avoir invité une personne qui ne recevra rien).
 */
export function cleanGuestEmails(v: unknown, bookerEmail: string): string[] | null {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || v.length > 20) return null;
  const out: string[] = [];
  for (const raw of v) {
    if (typeof raw !== 'string') return null;
    if (!raw.trim()) continue;
    const email = cleanBookingEmail(raw);
    if (!email) return null;
    if (email !== bookerEmail && !out.includes(email)) out.push(email);
  }
  return out.length > MAX_GUESTS ? null : out;
}
